import {
  Bucket,
  findMatchingBucketIndex,
  PropertyAttribute,
} from "@cartographerio/atlas-map";
import { Feature, featureProperty } from "@cartographerio/geometry";
import { Box, Flex, Stack } from "@chakra-ui/react";
import React, { ReactElement, useMemo } from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import InspectorHeading from "./InspectorHeading";

interface DistributionChartProps {
  attribute: PropertyAttribute;
  buckets: Bucket[];
  features: Feature[];
}

function Placeholder() {
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      bg="gray.100"
      px="4"
      fontSize="xs"
      h={160}
    >
      Shift-click and drag to select features
    </Flex>
  );
}

export default function DistributionChart(
  props: DistributionChartProps
): ReactElement {
  const { features, attribute, buckets } = props;

  const data = useMemo(() => {
    return features.reduce(
      (data, feature) => {
        const bucketIndex = findMatchingBucketIndex(
          buckets,
          featureProperty(feature, attribute.propertyName)
        );
        if (bucketIndex >= 0 && bucketIndex < data.length) {
          data[bucketIndex].count++;
        }
        return data;
      },
      buckets.map((bucket, index) => ({
        name: index + 1,
        count: 0,
        markerStyle: bucket.marker,
      }))
    );
  }, [attribute.propertyName, buckets, features]);

  return (
    <Stack spacing="2">
      <InspectorHeading>Distribution</InspectorHeading>
      {features.length === 0 ? (
        <Placeholder />
      ) : (
        <Box position="relative">
          <BarChart data={data} width={290} height={160}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={false} height={2} />
            <YAxis
              dataKey="count"
              allowDecimals={false}
              scale="linear"
              width={30}
            />
            <Bar dataKey="count" isAnimationActive={false}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.markerStyle.normal.fillColor}
                  // stroke={entry.markerStyle.normal.strokeColor}
                  // strokeWidth={entry.markerStyle.normal.strokeWidth}
                />
              ))}
            </Bar>
          </BarChart>
        </Box>
      )}
    </Stack>
  );
}
